import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_TM } from '@op/utils/format';
import { gotoDownload } from '@opm/pages/Download';
import { getCompetitorsAdCreativesList } from '@opm/services';
import { useUpdateEffect } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';
import { SortBy } from 'tea-component/lib/table/addons';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { useStableValue } from '@opm/utils/stableKey';
import useTabHistory from '@op/hooks/useTabHistory';

/**
 * group_by 映射：ads → id（广告视图），creatives → creative_id（素材视图）
 */
const GROUP_BY_MAP: Record<string, string> = {
  ads: 'creative_id',
  creatives: 'creative_id',
};

/**
 * Competitors Ad Creatives 统一数据管理 Hook
 *
 * 设计原则：
 * - 卡片和表格共用同一份请求数据（不缓存）
 * - 卡片视图：下拉加载（infinite scroll），累积所有页数据
 * - 表格视图：分页器翻页，只取当前页数据
 * - 视图切换时：清空数据，重置 page:1 重新请求
 */
export function useCompetitorsAdCreativesData({
  filter,
  defaultPageInfo = { page: 1, page_size: 20 },
  defaultViewMode = 'table',
}: {
  filter: any;
  defaultPageInfo?: { page: number; page_size: number };
  defaultViewMode?: 'table' | 'card';
}) {
  // ─── 搜索关键字 ───
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');

  // ─── AI Tags 搜索关键词（分号分隔） ───
  const [searchVideoTagsRef, searchVideoTags, setSearchVideoTags] = useStateRef<
    string[]
  >([]);

  // ─── Show Tagged Videos Only ───
  const [hasVideoTagsRef, hasVideoTags, setHasVideoTags] =
    useStateRef<boolean>(false);

  // ─── 视图类型: ads / creatives ───
  const { tab: type, onActive } = useTabHistory({ defaultValue: 'ads' });
  function setType(value: string) {
    onActive({ id: value });
  }

  // ─── 视图模式: table / card ───
  const [viewModeRef, viewMode, setViewMode] = useStateRef<'table' | 'card'>(
    defaultViewMode,
  );

  // ─── 排序状态 ───
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: 'desc',
    by: 'impression_number',
  });

  // ─── 分页 ───
  const {
    pageInfo,
    changePageInfo,
    resetPageInfo,
    nextPage,
    changePageInfoNoRender,
  } = usePageInfo({
    defaultPageInfo,
  });

  // ─── 卡片累积数据 ───
  const [cardDataRef, cardData, setCardData] = useStateRef<any[]>([]);

  // ─── 表格当前页数据 ───
  const [tableRecords, setTableRecords] = useState<any[]>([]);

  // ─── 共享状态 ───
  const [total, setTotal] = useState(0);
  const [maxStats, setMaxStats] = useState<Record<string, number>>({});

  // ─── 请求状态 ───
  const { wrapperCancelRequest, loading, loadingRef } = useCancelRequestHook();

  // ─── 重置所有数据 + 分页（筛选条件或视图切换时调用） ───
  function resetAllData() {
    setCardData([]);
    setTableRecords([]);
    setTotal(0);
    resetPageInfo();
  }

  // ─── 切换排序 ───
  function changeSortBy(newSortBy: SortBy<string>) {
    setSortBy(newSortBy);
    resetAllData();
  }

  // ─── 切换 type (ads / creatives) ───
  function changeType(val: string) {
    setType(val);
    resetAllData();
  }

  // ─── 切换关键字 ───
  function changeKeyword(val: string) {
    setKeyword(val);
    resetAllData();
  }

  // ─── 切换 AI Tags 搜索 ───
  function changeSearchVideoTags(val: string[]) {
    setSearchVideoTags(val);
    resetAllData();
  }

  // ─── 切换 Show Tagged Only ───
  function changeHasVideoTags(val: boolean) {
    setHasVideoTags(val);
    resetAllData();
  }

  // ─── 切换视图模式 ───
  // 切换时清空数据，从 page:1 重新加载
  function changeViewMode(mode: 'table' | 'card') {
    if (mode === viewModeRef.current) return;
    setViewMode(mode);
    // 清空数据，从第 1 页重新请求
    setCardData([]);
    setTableRecords([]);
    setTotal(0);
    // resetPageInfo 会将 page 重置为 1 并触发渲染 → useEffect 发起请求
    resetPageInfo();
  }

  // ─── 卡片视图：触底加载下一页 ───
  function loadNextPage() {
    // 同步守卫：loadingRef 是 useStateRef 返回的 ref，同步读取最新 loading 状态
    if (loadingRef.current) return;
    // 非卡片模式不触发
    if (viewModeRef.current !== 'card') return;
    // 已加载全部数据
    const loadedCount = cardDataRef.current?.length || 0;
    if (loadedCount >= total && total > 0) return;
    nextPage();
  }

  // ─── 表格视图：切换分页 ───
  function changeTablePageInfo(newPageInfo?: {
    page: number;
    page_size: number;
  }) {
    // page_size 变化时清空表格数据
    // if (newPageInfo && newPageInfo.page_size !== pageInfo.page_size) {
    // }
    setTableRecords([]);
    changePageInfo(newPageInfo);
  }

  // ─── 稳定输入引用（深比较，值不变则引用不变） ───
  const stablePageInfo = useStableValue(pageInfo);
  const stableFilter = useStableValue(filter);

  // ─── 筛选条件参数（不含分页） ───
  useUpdateEffect(() => {
    if (!stableFilter) return;
    resetAllData();
  }, [stableFilter]);

  // ─── 完整请求参数（含分页） ───
  const params = useMemo(() => {
    if (!stableFilter) return null;
    return {
      ...stableFilter,
      search_text: keywordRef.current,
      search_video_tags:
        viewModeRef.current === 'table' ? searchVideoTagsRef.current : [],
      has_video_tags: hasVideoTagsRef.current,
      group_by: GROUP_BY_MAP[type] || 'creative_id',
      sort_item: sortByRef.current?.by,
      sort_model: sortByRef.current?.order,
      ...stablePageInfo,
    };
  }, [stablePageInfo, stableFilter, sortBy, keyword, searchVideoTags, hasVideoTags]);
  
  // ─── 发起请求 ───
  function request() {
    if (!params) return;

    wrapperCancelRequest(getCompetitorsAdCreativesList, params)
      .then((response: any) => {
        const { list = [], total = 0, max = {} } = response || {};

        if (viewModeRef.current === 'card') {
          // 卡片模式：累积数据
          if (pageInfo.page === 1) {
            setCardData(list);
          } else {
            setCardData([...(cardDataRef.current || []), ...list]);
          }
        } else {
          // 表格模式：替换当前页数据
          setTableRecords(list);
        }

        setTotal(total);
        setMaxStats(max);
      })
      .catch(() => {
        // 请求被取消或失败，卡片模式回退页码
        if (viewModeRef.current === 'card' && pageInfo.page > 1) {
          const prevPage = Math.max(
            1,
            Math.ceil((cardDataRef.current?.length || 0) / pageInfo.page_size),
          );
          changePageInfoNoRender({ ...pageInfo, page: prevPage });
        }
      });
  }

  // ─── 监听请求参数变化发起请求 ───
  useEffect(() => {
    if (!params) return;

    request();
  }, [params, viewMode]);

  // ─── 下载 ───
  function download() {
    if (!params) return;
    DBOM_POINT.btn('Download', { type });
    gotoDownload({
      key: 'MarketingCompetitorsAdCreativesDownload',
      fileName: `Ad_Creatives_${DBO_TM.timeTitle(params)}`,
      filter: { ...params },
    });
  }

  // ─── 卡片视图数据 ───
  const cardRecords = cardData ?? [];

  // ─── 空状态 ───
  const currentRecords = viewMode === 'card' ? cardRecords : tableRecords;
  const isEmpty = !loading && currentRecords.length === 0;

  // ─── 首次加载 ───
  const isFirstLoading =
    loading && pageInfo.page === 1 && currentRecords.length === 0;

  // ─── 卡片分页加载（后续页加载中） ───
  const isPaginationLoading =
    loading && viewMode === 'card' && cardRecords.length > 0;

  return {
    // 卡片视图数据
    cardRecords,
    // 表格视图数据
    tableRecords,
    // 共享
    total,
    maxStats,
    pageInfo,
    changePageInfo: changeTablePageInfo,
    resetPageInfo,
    // 卡片触底加载
    nextPage: loadNextPage,
    // 加载状态
    loading,
    isFirstLoading,
    isEmpty,
    isPaginationLoading,
    // 排序
    sortBy,
    setSortBy: changeSortBy,
    // 下载
    download,
    // 搜索
    keyword,
    setKeyword: changeKeyword,
    // 视图类型
    type,
    setType: changeType,
    // 视图模式
    viewMode,
    setViewMode: changeViewMode,
    // AI Tags 搜索
    searchVideoTags,
    setSearchVideoTags: changeSearchVideoTags,
    // Show Tagged Only
    hasVideoTags,
    setHasVideoTags: changeHasVideoTags,
  };
}
